import React, { useMemo, useState } from 'react'
import { Lock } from 'lucide-react'
import { computeChecklist, SMR_P } from './smrFormPanelCore'
import {
  SmrV2PartMatter,
  SmrV2PartEntity,
  SmrV2PartSubject,
  SmrV2PartOtherParties,
  SmrV2PartUnidentified,
  SmrV2PartTransactions,
  SmrV2PartAdditional,
} from './SmrFormPanelV2Parts'

const PARTS = [
  { id: 'A', label: 'Details of the Matter', short: 'Matter' },
  { id: 'B', label: 'Reporting Entity', short: 'Entity' },
  { id: 'C', label: 'Suspicious Person', short: 'Subject' },
  { id: 'D', label: 'Other Parties', short: 'Others' },
  { id: 'E', label: 'Unidentified', short: 'Unid.' },
  { id: 'F', label: 'Transactions', short: 'Txns' },
  { id: 'G', label: 'Additional Details', short: 'Narrative' },
]

const noop = () => {}

/**
 * Read-only AUSTRAC Part A–G view for filed SMRs (case_form_submissions snapshot).
 * Same navigation as investigation SmrFormPanelV2; no edit, save, or live case fetches.
 */
export default function SmrFormPanelV2ReadOnly({
  draft,
  caseNumber,
  patronName,
  memberId,
  visitorId,
  masterProfileId,
  subjectType,
  filedAt,
  submittedBy,
  submissionId,
}) {
  const [activePart, setActivePart] = useState('A')
  const [findingsDetail, setFindingsDetail] = useState(true)

  const formFormat = draft?.form_format === 'rules_2025' ? 'rules_2025' : 'legacy'
  const checklist = useMemo(() => computeChecklist(draft, null), [draft])
  const completionPct = checklist.total ? Math.round((checklist.passed / checklist.total) * 100) : 0

  if (!draft || typeof draft !== 'object') {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-slate-600 text-sm bg-white">
        No submission payload on this filing record.
      </div>
    )
  }

  const ri = draft.reportInfo || {}
  const co = draft.completingOfficer || {}
  const sc = draft.suspicionContact || {}
  const meta = draft.meta || {}
  const dv = meta.dateRangeValidation
  const partIndex = PARTS.findIndex((p) => p.id === activePart)
  const activeMeta = PARTS.find((p) => p.id === activePart)
  const patronPrimary =
    masterProfileId || (subjectType === 'member' ? memberId : subjectType === 'visitor' ? visitorId : null) || memberId || visitorId
  const clubOrVmsId =
    masterProfileId && subjectType === 'member' && memberId
      ? memberId
      : masterProfileId && subjectType === 'visitor' && visitorId
        ? visitorId
        : null

  const partBody = (() => {
    switch (activePart) {
      case 'A':
        return (
          <SmrV2PartMatter
            readOnly
            draft={draft}
            formFormat={formFormat}
            updatePath={noop}
            toggleB4={noop}
            ri={ri}
          />
        )
      case 'B':
        return (
          <SmrV2PartEntity
            readOnly
            draft={draft}
            formFormat={formFormat}
            updatePath={noop}
            ri={ri}
            co={co}
            sc={sc}
            previousSmrCount={null}
          />
        )
      case 'C':
        return <SmrV2PartSubject readOnly draft={draft} formFormat={formFormat} updatePath={noop} />
      case 'D':
        return <SmrV2PartOtherParties draft={draft} />
      case 'E':
        return <SmrV2PartUnidentified readOnly draft={draft} memberId={memberId} updatePath={noop} />
      case 'F':
        return (
          <SmrV2PartTransactions
            readOnly
            draft={draft}
            formFormat={formFormat}
            updateTransaction={noop}
            removeTransactionRow={noop}
            addTransactionRow={noop}
          />
        )
      case 'G':
        return (
          <SmrV2PartAdditional
            readOnly
            draft={draft}
            formFormat={formFormat}
            updatePath={noop}
            toggleB2={noop}
            narrativeGuide={false}
            setNarrativeGuide={noop}
            findingsDetail={findingsDetail}
            setFindingsDetail={setFindingsDetail}
            addAgencyRow={noop}
            updateAgencyRow={noop}
            addOnlineRow={noop}
            updateOnlineRow={noop}
            downloadEvidenceFile={noop}
            checklist={checklist}
          />
        )
      default:
        return null
    }
  })()

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden bg-[#f8fafb] shadow-sm">
      <div
        className="px-4 py-4 sm:px-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
        style={{ background: `linear-gradient(135deg, ${SMR_P.pri} 0%, ${SMR_P.priD} 100%)` }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: SMR_P.acc, color: SMR_P.priD }}
          >
            <Lock className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="text-base font-bold text-white truncate">Filed Suspicious Matter Report</div>
            <div className="text-xs text-white/60">Immutable submission — long-term registry record</div>
            <div className="text-[11px] text-white/50 mt-1 truncate">
              {caseNumber && <span className="font-mono mr-2">{caseNumber}</span>}
              {patronName}
            </div>
            {patronPrimary && (
              <div className="text-[11px] text-white/60 mt-0.5 truncate font-mono">
                <span title="Patron / master profile ID">{patronPrimary}</span>
                {clubOrVmsId && (
                  <>
                    <span className="text-white/50 font-sans">
                      {' '}
                      · {subjectType === 'member' ? 'Member' : 'Visitor'}{' '}
                    </span>
                    <span>{clubOrVmsId}</span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="text-right text-xs text-white/80 shrink-0">
          {filedAt && <div>Filed {new Date(filedAt).toLocaleString()}</div>}
          {submittedBy && <div>By {submittedBy}</div>}
          {submissionId != null && <div className="font-mono text-white/50 mt-0.5">Submission #{submissionId}</div>}
        </div>
      </div>

      <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-xs text-amber-900">
        This is the archived copy filed at submission. It is not linked to an active investigation workspace.
      </div>

      <div className="bg-white border-b border-slate-100 px-4 py-3 flex flex-wrap items-center gap-3 text-xs text-slate-600">
        <span className="font-semibold">Format:</span>
        <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-800 font-medium">
          {formFormat === 'rules_2025' ? 'Rules 2025' : 'Legacy'}
        </span>
        {meta.reportDeadline && (
          <span className="sm:ml-auto">
            <span className="font-semibold">Filing deadline (at submit): </span>
            {meta.reportDeadline}
          </span>
        )}
        <span>
          <span className="font-semibold">Checklist at filing: </span>
          {completionPct}%
        </span>
      </div>

      {dv && (
        <div
          className={`mx-4 mt-3 rounded-lg border px-3 py-2 text-sm ${
            dv.valid ? 'bg-green-50 border-green-200 text-green-900' : 'bg-red-50 border-red-200 text-red-900'
          }`}
        >
          <div className="font-bold">Transaction date range (3-day rule)</div>
          <p className="mt-1 text-xs opacity-90">{dv.message}</p>
        </div>
      )}

      <div className="bg-white border-b border-slate-100 px-2 sm:px-3 flex gap-0 overflow-x-auto">
        {PARTS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setActivePart(p.id)}
            className={`shrink-0 px-3 sm:px-4 py-3 text-xs sm:text-[13px] font-semibold border-b-2 transition-colors ${
              activePart === p.id ? 'text-[#0a5169] border-[#0a5169]' : 'text-slate-400 border-transparent hover:text-slate-600'
            }`}
          >
            <span className="hidden sm:inline">Part {p.id} · </span>
            <span className="sm:hidden"> {p.id} </span>
            <span className="hidden md:inline">{p.label}</span>
            <span className="md:hidden">{p.short}</span>
          </button>
        ))}
      </div>

      <div className="max-w-3xl mx-auto px-4 py-5">
        <div className="bg-white rounded-lg border border-slate-100 px-4 py-3 mb-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Part {activePart}</div>
            <div className="text-base font-bold text-slate-800">{activeMeta?.label}</div>
          </div>
          <span className="text-[10px] font-bold uppercase px-2 py-1 rounded bg-emerald-50 text-emerald-800 border border-emerald-100">
            Filed copy
          </span>
        </div>

        <div className="bg-white rounded-lg border border-slate-100 p-4 sm:p-5">{partBody}</div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-5">
          <button
            type="button"
            onClick={() => partIndex > 0 && setActivePart(PARTS[partIndex - 1].id)}
            disabled={partIndex <= 0}
            className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-600 disabled:opacity-40"
          >
            ← Previous part
          </button>
          <button
            type="button"
            onClick={() => partIndex < PARTS.length - 1 && setActivePart(PARTS[partIndex + 1].id)}
            disabled={partIndex >= PARTS.length - 1}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-40 sm:order-last"
            style={{ background: SMR_P.pri }}
          >
            Next part →
          </button>
        </div>
      </div>
    </div>
  )
}
